import React, { useEffect, useState, useRef } from 'react';
import { normalizeSearch } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Download, Calendar } from 'lucide-react';
import type { FavoriteItem } from '../../types/favorites';
import { useHeader } from '../../context/HeaderContext';
import { SearchAndFilterBar } from '../standard/SearchAndFilterBar';
import { FilterDrawer } from '../shared/FilterDrawer';
import type { FilterState } from '../shared/FilterDrawer';
import { FavoritesList } from './FavoritesList';
import { FavoritesMap } from './FavoritesMap';
import { ViewToggle } from '../shared/ViewToggle';
import { FavoritesDetailCard } from './FavoritesDetailCard';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { useAirtableQuery } from '../../hooks/useAirtableQuery';
import { useTranslatedContent } from '../../hooks/useTranslatedContent';
import { useFavorites } from '../../hooks/useFavorites';
import type { SupportedLanguages } from '../../i18n';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { generateFavoritesPDF } from '../../utils/pdfGenerator';
import { SectionHeader } from '../layout/SectionHeader';
import { resolveColor } from '../../utils/colorUtils';
import { Toast } from '../shared/Toast';
import { CalendarModal } from '../shared/CalendarModal';
import { useAnalytics } from '../../hooks/useAnalytics';

const FavoritesSection: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { setTitle } = useHeader();
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const analytics = useAnalytics({
    sectionId: 'favorites',
    sectionName: 'Favorites',
    sectionType: 'favorites'
  });
  
  const [filters, setFilters] = useState<FilterState>({ category: '', categories: [], types: [], priceFilter: [] });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false);
  const [view, setView] = useState<'map' | 'list'>('list');
  const [selectedItem, setSelectedItem] = useState<FavoriteItem | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);

  // Use favorites hook
  const { favorites, removeFavorite } = useFavorites();

  // Data fetching hooks
  const { data: config, isLoading: configLoading } = useAirtableQuery.useConfig();
  const { data: translations, isLoading: translationsLoading } = useAirtableQuery.useTranslations(i18n.language as SupportedLanguages);

  // Get specific translations from trad_txt
  const { translations: pageTranslations } = useTranslatedContent({
    terms: ['favorites', 'no_favorites', 'download_favorites', 'add_to_calendar', 'calendar_success', 'calendar_error', 'no_events_with_dates', 'generating_pdf', 'pdf_success']
  });

  // Track section view
  useEffect(() => {
    analytics.trackSectionView(view);
  }, [view, analytics]);

  // Update title once when translations are available
  useEffect(() => {
    if (pageTranslations?.favorites) {
      const title = pageTranslations.favorites[i18n.language as SupportedLanguages] || pageTranslations.favorites.fr;
      setTitle(title);
    }
  }, [pageTranslations, i18n.language, setTitle]);

  // Event handlers
  const handleBackClick = () => {
    navigate('/');
  };

  const handleFiltersChange = (newFilters: FilterState) => {
    analytics.trackFilter('filter_change', JSON.stringify(newFilters));
    setFilters(newFilters);
    setSelectedItem(null);
  };

  const handleCategorySelect = (category: string) => {
    handleFiltersChange({ ...filters, category, types: [] });
  };

  const handleViewChange = (newView: 'map' | 'list') => {
    analytics.trackSectionView(newView);
    setView(newView);
  };

  const handleItemClick = (item: FavoriteItem) => {
    analytics.trackItemAction(
      item.id,
      'item_click',
      item.sourceType,
      {
        item_name: item.data.name,
        category: item.data.category,
        type: item.data.type,
        section_name: 'Favorites',
        section_type: 'favorites'
      }
    );

    if (view === 'map' && mapInstanceRef.current && item.data.latitude && item.data.longitude) {
      mapInstanceRef.current.panTo({ lat: item.data.latitude, lng: item.data.longitude });
      const currentZoom = mapInstanceRef.current.getZoom() || 12;
      if (currentZoom < 15) {
        mapInstanceRef.current.setZoom(15);
      }
    }

    setSelectedItem(item);
  };

  const handleRemoveFavorite = (itemId: string) => {
    analytics.trackItemAction(
      itemId,
      'remove_favorite',
      'favorite',
      {
        source: 'favorites_section',
        section_id: 'favorites',
        section_name: 'Favorites',
        section_type: 'favorites'
      }
    );
    removeFavorite(itemId);
    setSelectedItem(null);
  };

  const handleDownloadPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      // Show toast for PDF generation start
      setToastMessage(pageTranslations?.generating_pdf?.[i18n.language] || 'Generating PDF...');
      setShowToast(true);
      
      analytics.trackEvent('interaction', 'download_favorites', {
        section_id: 'favorites',
        section_name: 'Favorites',
        section_type: 'favorites',
        favorites_count: favorites.length,
        language: i18n.language
      });
      
      await generateFavoritesPDF(favorites, config, translations, i18n.language as SupportedLanguages);
      
      // Show success toast
      setToastMessage(pageTranslations?.pdf_success?.[i18n.language] || 'PDF generated successfully');
      setShowToast(true);
    } catch (error) {
      console.error('Error generating PDF:', error);
      
      // Show error toast
      setToastMessage(error instanceof Error ? error.message : 'Error generating PDF');
      setShowToast(true);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleAddToCalendar = async () => {
    try {
      // Track the event
      analytics.trackEvent('interaction', 'add_favorites_to_calendar', {
        section_id: 'favorites',
        section_name: 'Favorites',
        section_type: 'favorites',
        favorites_count: favorites.length,
        language: i18n.language
      });

      // Filter favorites with date and time
      const eventsWithDates = favorites.filter(item => 
        (item.sourceType === 'event' || item.sourceType === 'event-api') && 
        item.data.date && 
        item.data.hour
      );

      if (eventsWithDates.length === 0) {
        setToastMessage(pageTranslations?.no_events_with_dates?.[i18n.language] || 
                       'No events with dates found in your favorites');
        setShowToast(true);
        return;
      }

      // Show calendar selection modal
      if (eventsWithDates.length > 0 && eventsWithDates[0].data) {
        setSelectedItem(eventsWithDates[0]);
        setShowCalendarModal(true);
      }
    } catch (error) {
      console.error('Error preparing calendar events:', error);
      setToastMessage(pageTranslations?.calendar_error?.[i18n.language] || 
                     'Error adding events to calendar');
      setShowToast(true);
    }
  };

  // Loading state
  if (configLoading || translationsLoading) {
    return <LoadingIndicator />;
  }

  // Error state
  if (!config || !translations) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <p className="text-center mb-4">Erreur lors du chargement des données</p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }

  const categoryColors = React.useMemo(() => {
    if (!config || !favorites.length) return new Map<string, string>();
    const colors = config.colors_map?.split(',').map((c: string) => c.trim()) || [];
    const uniqueCategories = Array.from(new Set(favorites.map(f => f.data.category)));
    const colorMap = new Map<string, string>();
    uniqueCategories.forEach((category, index) => {
      colorMap.set(category, colors[index % colors.length]);
    });
    return colorMap;
  }, [config, favorites]);

  const activeFiltersCount =
    (filters.category ? 1 : 0) + filters.types.length + filters.priceFilter.length;

  const filteredFavorites = React.useMemo(() => {
    return favorites.filter(item => {
      const categoryMatch = !filters.category || item.data.category === filters.category;
      const typeMatch = filters.types.length === 0 || filters.types.includes(item.data.type || '');
      const q = normalizeSearch(searchQuery.trim());
      const searchMatch =
        !q ||
        normalizeSearch(item.data.name).includes(q) ||
        normalizeSearch(item.data.category || '').includes(q) ||
        normalizeSearch(item.data.type || '').includes(q);
      return categoryMatch && typeMatch && searchMatch;
    });
  }, [favorites, filters, searchQuery]);

  const mainLocation = React.useMemo(() => ({
    name: config.name,
    latitude: config.latitude,
    longitude: config.longitude,
    fulladress: config.fulladress
  }), [config.name, config.latitude, config.longitude, config.fulladress]);

  // Check if there are any favorites with dates
  const hasEventWithDates = favorites.some(item => 
    (item.sourceType === 'event' || item.sourceType === 'event-api') && 
    item.data.date && item.data.hour
  );

  return (
    <div 
      className="min-h-screen"
      style={{ 
        backgroundColor: resolveColor(config['sectionBack-color'], config, '#f9fafb')
      }}
    >
      <SectionHeader
        config={config}
        title={pageTranslations?.favorites?.[i18n.language as SupportedLanguages] || pageTranslations?.favorites?.fr || 'Favoris'}
        onBackClick={handleBackClick}
        translations={translations}
      />

      {/* Filter */}
      <div className="filter-block" style={{ backgroundColor: config['major-color'] }}>
        <SearchAndFilterBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onFiltersOpen={() => setIsFilterDrawerOpen(true)}
          activeFiltersCount={activeFiltersCount}
          config={config}
          translations={translations}
        />
      </div>

      {/* Main content */}
      <main className="main-content">
        {/* Action buttons if favorites exist */}
        {favorites.length > 0 && (
          <div className="fixed right-4 bottom-24 z-[150] flex flex-col gap-2">
            {/* Add to Calendar button - only show if there are events with dates */}
            {hasEventWithDates && (
              <button
                onClick={handleAddToCalendar}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white shadow-lg hover:opacity-90 transition-opacity"
                style={{ 
                  backgroundColor: config['major-color'],
                  borderRadius: config.border_radius 
                }}
              >
                <Calendar size={20} />
                <span className="text-sm font-medium">
                  {pageTranslations?.add_to_calendar?.[i18n.language as SupportedLanguages] || 'Add to Calendar'}
                </span>
              </button>
            )}
            
            {/* Download PDF button */}
            <button
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white shadow-lg hover:opacity-90 transition-opacity ${isGeneratingPDF ? 'opacity-70 cursor-not-allowed' : ''}`}
              style={{ 
                backgroundColor: config['major-color'],
                borderRadius: config.border_radius 
              }}
            >
              <Download size={20} />
              <span className="text-sm font-medium">
                {isGeneratingPDF 
                  ? (pageTranslations?.generating_pdf?.[i18n.language as SupportedLanguages] || 'Generating...') 
                  : (pageTranslations?.download_favorites?.[i18n.language as SupportedLanguages] || 'Download')}
              </span>
            </button>
          </div>
        )}

        {view === 'list' ? (
          <FavoritesList
            items={filteredFavorites}
            onItemClick={handleItemClick}
            onRemoveFavorite={handleRemoveFavorite}
            config={config}
            translations={translations}
            categoryColors={categoryColors}
            onCategorySelect={handleCategorySelect}
            onTypeSelect={(type) => handleFiltersChange({ ...filters, types: type ? [type] : [] })}
          />
        ) : (
          <div 
            className="fixed inset-0 pt-[94px] pb-[40px]"
            style={{ zIndex: 1 }}
          >
            <FavoritesMap
              items={filteredFavorites}
              config={config}
              translations={translations}
              onItemClick={handleItemClick}
              mainLocation={mainLocation}
              categoryColors={categoryColors}
              mapInstanceRef={mapInstanceRef}
            />
          </div>
        )}
      </main>

      {/* Fixed view toggle button */}
      <div 
        className="fixed left-1/2 -translate-x-1/2 z-[150] bottom-[58px]"
      >
        <ViewToggle
          view={view}
          onChange={handleViewChange}
          config={config}
          translations={translations}
          sectionType="favorites"
        />
      </div>

      {/* Modals */}
      {selectedItem && (
        <FavoritesDetailCard
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onRemoveFavorite={handleRemoveFavorite}
          config={config}
          translations={translations}
          categoryColors={categoryColors}
          onCategorySelect={handleCategorySelect}
          onTypeSelect={(type) => handleFiltersChange({ ...filters, types: type ? [type] : [] })}
        />
      )}

      {/* Calendar Modal */}
      {selectedItem && showCalendarModal && (
        <CalendarModal
          isOpen={showCalendarModal}
          onClose={() => setShowCalendarModal(false)}
          event={selectedItem.data}
          config={config}
          translations={translations}
          description={selectedItem.translations?.descriptions?.[i18n.language] || selectedItem.data.description}
        />
      )}

      {/* Toast notification */}
      {showToast && (
        <Toast
          message={toastMessage}
          type="success"
          onClose={() => setShowToast(false)}
          duration={3000}
          icon={isGeneratingPDF ? <Download size={18} className="text-white" /> : <Calendar size={18} className="text-white" />}
          config={config}
        />
      )}

      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        items={favorites.map(f => f.data)}
        config={config}
        translations={translations}
        categoryColors={categoryColors}
      />
    </div>
  );
};

export default FavoritesSection;
export { FavoritesSection };